// ═══════════════════════════════════════════════════════════════════════
// Invoiced Jobs Report
// ═══════════════════════════════════════════════════════════════════════
// Shows all jobs that have been invoiced, with filters for date range,
// invoice number, template, target system, and account.
// Queries the invoiced_jobs_view which joins job_invoice_records with
// taxicaller_data, crm_accounts, and export_runs.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Receipt, Search, Hash, RefreshCw, Loader2, ChevronDown, ChevronRight,
  ChevronLeft, Calendar, Download, FileText, Filter, X, BarChart3,
  ArrowUp, ArrowDown, AlertTriangle, Building2,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDate, formatDateTime } from '@/lib/dates';
import { ACCOUNT_SERIES } from '@/lib/accountSeries';

// ─── Types ──────────────────────────────────────────────────────────

interface InvoicedJob {
  invoice_record_id: string;
  tc_id: string;
  job_id: string;
  invoice_number: number;
  invoice_group_key: string | null;
  export_run_id: string | null;
  template_name: string | null;
  target_system: string | null;
  invoiced_at: string;
  invoice_created_at: string;
  project: string | null;
  account_id: string | null;
  reference: string | null;
  passenger_name: string | null;
  pick_up_location: string | null;
  drop_off_location: string | null;
  pu_date: string | null;
  pu_time: string | null;
  do_time: string | null;
  tc_vehicle_number: string | null;
  tc_driver_name: string | null;
  grand_total: number | null;
  direct_settled: number | null;
  tc_cost_code: string | null;
  stage: string | null;
  fleet: string | null;
  trip_total: number | null;
  job_km: number | null;
  account_name: string | null;
  billed_status: string | null;
  billing_name: string | null;
  export_file_name: string | null;
  exported_by: string | null;
  exported_at: string | null;
  export_row_count: number | null;
  export_grand_total: number | null;
  export_notes: string | null;
  account_series: string | null;
}

interface InvoicedJobsReportProps {
  open: boolean;
  onClose: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────

function getCurrentMonthRange() {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const last = new Date(y, m + 1, 0).getDate();
  const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { from, to };
}

function getLastMonthRange() {
  const now = new Date();
  const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
  const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const last = new Date(y, m + 1, 0).getDate();
  const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { from, to };
}

const fmt = (v: number | null) => v == null ? '-' : `$${Number(v).toFixed(2)}`;

const PAGE_SIZE = 50;

// ─── Component ──────────────────────────────────────────────────────

const InvoicedJobsReport: React.FC<InvoicedJobsReportProps> = ({ open, onClose }) => {
  const range = getCurrentMonthRange();

  // Filters
  const [dateFrom, setDateFrom] = useState(range.from);
  const [dateTo, setDateTo] = useState(range.to);
  const [invoiceNumberSearch, setInvoiceNumberSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('__all');
  const [targetFilter, setTargetFilter] = useState('__all');
  const [accountSearch, setAccountSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');
  const [seriesFilter, setSeriesFilter] = useState<string>('__all');

  // Data
  const [rows, setRows] = useState<InvoicedJob[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string>('invoiced_at');
  const [sortAsc, setSortAsc] = useState(false);

  // Metadata for filter dropdowns
  const [templateNames, setTemplateNames] = useState<string[]>([]);
  const [targetSystems, setTargetSystems] = useState<string[]>([]);

  // Expanded row
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Load filter metadata
  const loadMetadata = useCallback(async () => {
    try {
      const { data: templates } = await supabase
        .from('job_invoice_records')
        .select('template_name')
        .not('template_name', 'is', null);
      
      const { data: targets } = await supabase
        .from('job_invoice_records')
        .select('target_system')
        .not('target_system', 'is', null);

      if (templates) {
        const unique = [...new Set(templates.map(t => t.template_name).filter(Boolean))].sort();
        setTemplateNames(unique as string[]);
      }
      if (targets) {
        const unique = [...new Set(targets.map(t => t.target_system).filter(Boolean))].sort();
        setTargetSystems(unique as string[]);
      }
    } catch (err) {
      console.error('Failed to load filter metadata:', err);
    }
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('invoiced_jobs_view')
        .select('*', { count: 'exact' });

      // Date filter on pu_date (job date)
      if (dateFrom) query = query.gte('pu_date', dateFrom);
      if (dateTo) query = query.lte('pu_date', dateTo);

      // Invoice number filter
      if (invoiceNumberSearch.trim()) {
        const num = parseInt(invoiceNumberSearch.trim(), 10);
        if (!isNaN(num)) {
          query = query.eq('invoice_number', num);
        }
      }

      // Template filter
      if (templateFilter !== '__all') {
        query = query.eq('template_name', templateFilter);
      }

      // Target system filter
      if (targetFilter !== '__all') {
        query = query.eq('target_system', targetFilter);
      }

      // Account search
      if (accountSearch.trim()) {
        query = query.or(`account_id.ilike.%${accountSearch.trim()}%,account_name.ilike.%${accountSearch.trim()}%`);
      }

      // Job search
      if (jobSearch.trim()) {
        query = query.ilike('job_id', `%${jobSearch.trim()}%`);
      }

      // Series filter
      if (seriesFilter !== '__all') {
        query = query.eq('account_series', seriesFilter);
      }

      // Sort
      query = query.order(sortKey, { ascending: sortAsc });
      if (sortKey !== 'pu_date') query = query.order('pu_date', { ascending: false });

      // Pagination
      const from = page * PAGE_SIZE;
      query = query.range(from, from + PAGE_SIZE - 1);

      const { data, count, error } = await query;
      if (error) throw error;

      setRows((data || []) as InvoicedJob[]);
      setTotalCount(count || 0);
    } catch (err: any) {
      console.error('Failed to load invoiced jobs:', err);
      toast({ title: 'Error', description: err.message || 'Failed to load invoiced jobs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, invoiceNumberSearch, templateFilter, targetFilter, accountSearch, jobSearch, seriesFilter, sortKey, sortAsc, page]);

  useEffect(() => {
    if (open) {
      loadMetadata();
      loadData();
    }
  }, [open, loadData, loadMetadata]);

  // Summary stats
  const stats = useMemo(() => {
    let totalGrand = 0;
    const invoiceNumbers = new Set<number>();
    const accounts = new Set<string>();
    const templates = new Set<string>();

    for (const r of rows) {
      totalGrand += Number(r.grand_total || 0);
      invoiceNumbers.add(r.invoice_number);
      if (r.account_id) accounts.add(r.account_id);
      if (r.template_name) templates.add(r.template_name);
    }

    return {
      totalGrand,
      uniqueInvoices: invoiceNumbers.size,
      uniqueAccounts: accounts.size,
      uniqueTemplates: templates.size,
    };
  }, [rows]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(key === 'invoice_number'); }
    setPage(0);
  };

  // Export to CSV
  const handleExportCSV = useCallback(() => {
    if (rows.length === 0) return;

    const headers = [
      'Invoice #', 'Job ID', 'Account ID', 'Account Name', 'Passenger',
      'Pickup Date', 'Pickup', 'Dropoff', 'Grand Total', 'Template',
      'Target System', 'Invoiced At', 'Export File', 'Exported By',
    ];

    const csvRows = rows.map(r => [
      r.invoice_number,
      r.job_id,
      r.account_id || '',
      r.account_name || '',
      r.passenger_name || '',
      r.pu_date || '',
      r.pick_up_location || '',
      r.drop_off_location || '',
      r.grand_total != null ? Number(r.grand_total).toFixed(2) : '',
      r.template_name || '',
      r.target_system || '',
      r.invoiced_at ? formatDateTime(r.invoiced_at) : '',
      r.export_file_name || '',
      r.exported_by || '',
    ]);

    const csv = [
      headers.join(','),
      ...csvRows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoiced_jobs_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Exported', description: `${rows.length} invoiced job records exported to CSV` });
  }, [rows]);

  const applyQuickDate = (preset: 'this_month' | 'last_month' | 'all') => {
    setPage(0);
    if (preset === 'this_month') {
      const r = getCurrentMonthRange();
      setDateFrom(r.from); setDateTo(r.to);
    } else if (preset === 'last_month') {
      const r = getLastMonthRange();
      setDateFrom(r.from); setDateTo(r.to);
    } else {
      setDateFrom(''); setDateTo('');
    }
  };

  const clearFilters = () => {
    const r = getCurrentMonthRange();
    setDateFrom(r.from); setDateTo(r.to);
    setInvoiceNumberSearch('');
    setTemplateFilter('__all');
    setTargetFilter('__all');
    setAccountSearch('');
    setJobSearch('');
    setSeriesFilter('__all');
    setPage(0);
  };

  // Column definitions for the table
  const columns = [
    { key: 'invoice_number', label: 'Inv #', width: 70, align: 'center' as const },
    { key: 'job_id', label: 'Job ID', width: 90 },
    { key: 'account_id', label: 'Acct', width: 60, align: 'center' as const },
    { key: 'account_name', label: 'Account Name', width: 160 },
    { key: 'passenger_name', label: 'Passenger', width: 140 },
    { key: 'pu_date', label: 'Date', width: 90 },
    { key: 'pick_up_location', label: 'Pickup', width: 180 },
    { key: 'drop_off_location', label: 'Dropoff', width: 180 },
    { key: 'grand_total', label: 'Total', width: 80, align: 'right' as const },
    { key: 'template_name', label: 'Template', width: 120 },
    { key: 'invoiced_at', label: 'Invoiced', width: 100 },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-card border-border max-w-[95vw] w-[1400px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Receipt className="w-5 h-5 text-indigo-400" />
            Invoiced Jobs Report
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            View all jobs that have been invoiced, track billing history, and identify re-invoicing
          </DialogDescription>
        </DialogHeader>

        {/* ─── Summary Stats ─────────────────────────────────────── */}
        <div className="bg-secondary/30 border border-border/50 rounded-md px-4 py-2.5 flex flex-wrap items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Total Records:</span>
            <span className="font-semibold text-foreground">{totalCount.toLocaleString()}</span>
          </div>
          <div className="h-3 w-px bg-border" />
          <div className="flex items-center gap-1">
            <Hash className="w-3 h-3 text-indigo-400" />
            <span className="text-muted-foreground">Invoices (page):</span>
            <span className="font-semibold text-indigo-400">{stats.uniqueInvoices}</span>
          </div>
          <div className="h-3 w-px bg-border" />
          <div className="flex items-center gap-1">
            <Building2 className="w-3 h-3 text-emerald-400" />
            <span className="text-muted-foreground">Accounts:</span>
            <span className="font-semibold text-emerald-400">{stats.uniqueAccounts}</span>
          </div>
          <div className="h-3 w-px bg-border" />
          <div>
            <span className="text-muted-foreground">Grand Total (page):</span>
            <span className="font-semibold text-foreground ml-1">${stats.totalGrand.toFixed(2)}</span>
          </div>
        </div>

        {/* ─── Filters ───────────────────────────────────────────── */}
        <div className="space-y-2">
          {/* Quick date presets */}
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground uppercase">Quick:</span>
            {[
              { key: 'this_month', label: 'This Month' },
              { key: 'last_month', label: 'Last Month' },
              { key: 'all', label: 'All Time' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => applyQuickDate(p.key as any)}
                className="text-[10px] px-2 py-0.5 rounded-full border border-border/40 text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
              >
                {p.label}
              </button>
            ))}
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={clearFilters} className="h-6 text-[10px] text-muted-foreground">
              <X className="w-3 h-3 mr-1" /> Clear Filters
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportCSV} disabled={rows.length === 0} className="h-6 text-[10px] border-green-700/40 text-green-400 hover:bg-green-900/20">
              <Download className="w-3 h-3 mr-1" /> Export CSV
            </Button>
          </div>

          {/* Filter inputs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Job Date From</Label>
              <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} className="dark-input text-xs h-7" />
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Job Date To</Label>
              <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} className="dark-input text-xs h-7" />
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Invoice #</Label>
              <Input
                value={invoiceNumberSearch}
                onChange={e => { setInvoiceNumberSearch(e.target.value); setPage(0); }}
                placeholder="e.g. 1001"
                className="dark-input text-xs h-7"
              />
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Template</Label>
              <Select value={templateFilter} onValueChange={v => { setTemplateFilter(v); setPage(0); }}>
                <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All Templates</SelectItem>
                  {templateNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Target System</Label>
              <Select value={targetFilter} onValueChange={v => { setTargetFilter(v); setPage(0); }}>
                <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All Systems</SelectItem>
                  {targetSystems.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Account</Label>
              <Input
                value={accountSearch}
                onChange={e => { setAccountSearch(e.target.value); setPage(0); }}
                placeholder="ID or name..."
                className="dark-input text-xs h-7"
              />
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Job ID</Label>
              <Input
                value={jobSearch}
                onChange={e => { setJobSearch(e.target.value); setPage(0); }}
                placeholder="Search..."
                className="dark-input text-xs h-7"
              />
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Series</Label>
              <Select value={seriesFilter} onValueChange={v => { setSeriesFilter(v); setPage(0); }}>
                <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All</SelectItem>
                  {ACCOUNT_SERIES.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.key}-Series</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* ─── Data Table ────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto min-h-0 border border-border/60 rounded-md">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invoiced jobs...
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
              <Receipt className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">{totalCount === 0 ? 'No invoiced jobs found.' : 'No records match your filters.'}</p>
              <p className="text-xs mt-1 opacity-60">
                Jobs are recorded here when exported with invoice numbers assigned.
              </p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border">
                  {columns.map(col => (
                    <th
                      key={col.key}
                      className="px-2 py-2 text-[10px] font-medium text-muted-foreground whitespace-nowrap select-none cursor-pointer hover:text-foreground transition-colors bg-card border-b border-border"
                      style={{ minWidth: col.width, textAlign: col.align || 'left' }}
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="flex items-center gap-1" style={{ justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start' }}>
                        {col.label}
                        {sortKey === col.key ? (sortAsc ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />) : null}
                      </span>
                    </th>
                  ))}
                  <th className="px-2 py-2 text-[10px] font-medium text-muted-foreground bg-card border-b border-border w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isExpanded = expandedId === row.invoice_record_id;
                  return (
                    <React.Fragment key={row.invoice_record_id}>
                      <tr
                        className={`border-b border-border/50 hover:bg-secondary/30 transition-colors cursor-pointer ${isExpanded ? 'bg-primary/5' : ''}`}
                        onClick={() => setExpandedId(isExpanded ? null : row.invoice_record_id)}
                      >
                        <td className="px-2 py-1.5 text-center">
                          <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[10px] px-1.5 py-0 font-mono">
                            {row.invoice_number}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{row.job_id || '-'}</td>
                        <td className="px-2 py-1.5 text-center">
                          {row.account_id ? (
                            <Badge className={`text-[9px] px-1.5 py-0 ${
                              row.account_series === '5' ? 'bg-blue-900/40 text-blue-400 border-blue-700/50' :
                              row.account_series === '6' ? 'bg-emerald-900/40 text-emerald-400 border-emerald-700/50' :
                              row.account_series === '7' ? 'bg-purple-900/40 text-purple-400 border-purple-700/50' :
                              row.account_series === '8' ? 'bg-orange-900/40 text-orange-400 border-orange-700/50' :
                              'bg-secondary text-muted-foreground'
                            }`}>
                              {row.account_id}
                            </Badge>
                          ) : <span className="text-muted-foreground/40">-</span>}
                        </td>
                        <td className="px-2 py-1.5 truncate max-w-[160px]">{row.account_name || <span className="text-muted-foreground/40">-</span>}</td>
                        <td className="px-2 py-1.5 truncate max-w-[140px]">{row.passenger_name || <span className="text-muted-foreground/40">-</span>}</td>
                        <td className="px-2 py-1.5">{row.pu_date || '-'}</td>
                        <td className="px-2 py-1.5 truncate max-w-[180px]">{row.pick_up_location || <span className="text-muted-foreground/40">-</span>}</td>
                        <td className="px-2 py-1.5 truncate max-w-[180px]">{row.drop_off_location || <span className="text-muted-foreground/40">-</span>}</td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {row.grand_total != null ? (
                            <span className={Number(row.grand_total) === 0 ? 'text-muted-foreground/50' : ''}>{fmt(row.grand_total)}</span>
                          ) : '-'}
                        </td>
                        <td className="px-2 py-1.5">
                          {row.template_name ? (
                            <span className="text-xs truncate block max-w-[120px]">{row.template_name}</span>
                          ) : <span className="text-muted-foreground/40">-</span>}
                        </td>
                        <td className="px-2 py-1.5 text-[10px] text-muted-foreground">
                          {row.invoiced_at ? formatDate(row.invoiced_at) : '-'}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {isExpanded ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <tr className="bg-primary/5 border-b border-border/50">
                          <td colSpan={columns.length + 1} className="px-4 py-3">
                            <div className="grid grid-cols-3 gap-x-8 gap-y-1.5 text-xs">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Invoice Group Key:</span>
                                <span className="text-foreground font-mono text-[10px] truncate ml-2 max-w-[200px]">{row.invoice_group_key || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Target System:</span>
                                <span className="text-foreground">{row.target_system || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Stage:</span>
                                <span className={`${row.stage === 'Delivered' ? 'text-green-400' : row.stage === 'Cancelled' ? 'text-red-400' : 'text-foreground'}`}>
                                  {row.stage || '-'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Project:</span>
                                <span className="text-foreground">{row.project || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Reference:</span>
                                <span className="text-foreground">{row.reference || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Cost Code:</span>
                                <span className="text-foreground">{row.tc_cost_code || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Driver:</span>
                                <span className="text-foreground">{row.tc_driver_name || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Vehicle:</span>
                                <span className="text-foreground">{row.tc_vehicle_number || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Fleet:</span>
                                <span className="text-foreground">{row.fleet || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">PU Time:</span>
                                <span className="text-foreground">{row.pu_time ? row.pu_time.substring(0, 5) : '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">DO Time:</span>
                                <span className="text-foreground">{row.do_time ? row.do_time.substring(0, 5) : '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Trip Total:</span>
                                <span className="text-foreground">{fmt(row.trip_total)}</span>
                              </div>
                              {row.billing_name && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Billing Name:</span>
                                  <span className="text-foreground">{row.billing_name}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Billed Status:</span>
                                <span className={row.billed_status === 'Billed' ? 'text-green-400' : 'text-foreground'}>{row.billed_status || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">KM:</span>
                                <span className="text-foreground">{row.job_km != null ? `${Number(row.job_km).toFixed(1)} km` : '-'}</span>
                              </div>
                            </div>

                            {/* Export run info */}
                            {row.export_run_id && (
                              <div className="mt-3 pt-2 border-t border-border/30">
                                <p className="text-[10px] text-muted-foreground uppercase font-medium mb-1">Export Run Details</p>
                                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Export File:</span>
                                    <span className="text-foreground truncate ml-2">{row.export_file_name || '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Exported By:</span>
                                    <span className="text-foreground">{row.exported_by || '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Exported At:</span>
                                    <span className="text-foreground">{row.exported_at ? formatDateTime(row.exported_at) : '-'}</span>
                                  </div>
                                  {row.export_notes && (
                                    <div className="col-span-3 flex gap-2">
                                      <span className="text-muted-foreground flex-shrink-0">Notes:</span>
                                      <span className="text-foreground">{row.export_notes}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ─── Pagination ────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t border-border/40">
            <span className="text-xs text-muted-foreground">
              Page {page + 1} of {totalPages} ({totalCount.toLocaleString()} total records)
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className="text-muted-foreground h-7 text-xs">
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </Button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 7) pageNum = i;
                else if (page < 3) pageNum = i;
                else if (page > totalPages - 4) pageNum = totalPages - 7 + i;
                else pageNum = page - 3 + i;
                return (
                  <Button key={pageNum} size="sm" variant={pageNum === page ? 'default' : 'ghost'}
                    onClick={() => setPage(pageNum)}
                    className={`text-xs min-w-[28px] h-7 ${pageNum === page ? 'golden-button' : 'text-muted-foreground'}`}
                  >{pageNum + 1}</Button>
                );
              })}
              <Button size="sm" variant="ghost" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="text-muted-foreground h-7 text-xs">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end pt-1">
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground text-xs">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicedJobsReport;
